import { z } from "zod";
import {
  AssignmentDto,
  Channel,
  CoverageDto,
  FindingDto,
  GroupDto,
  MessageDto,
  ParticipantDto,
  PhotoDto,
  RecallDto,
  SectorDto,
  SosDto,
  SubjectPublicDto,
} from "./dto";
import { ActionStatus, ParticipantStatus, TrackingState } from "./enums";

// Action event log (ADR-0012). Every change of action state is one row in `action_events`
// with a per-action `seq` assigned in the writing transaction. The same envelope is:
//  - the SSE stream to the panel (`id:` = seq),
//  - the downlink to phones in the sync response (filtered per participant),
//  - the source for history replay and audit.
// Payloads never contain medical notes or session tokens.

const Id = z.uuid();
const Timestamp = z.iso.datetime({ offset: true });

export const actionEventPayloads = {
  "action.updated": z.object({
    name: z.string(),
    hqPhone: z.string().nullable(),
    kind: z.enum(["real", "exercise"]),
  }),
  "action.status": z.object({ status: ActionStatus }),

  "group.created": z.object({ group: GroupDto }),
  "group.updated": z.object({ group: GroupDto }),
  "group.deleted": z.object({ groupId: Id }),

  "subject.updated": z.object({ subject: SubjectPublicDto }),

  "sector.created": z.object({ sector: SectorDto }),
  "sector.updated": z.object({ sector: SectorDto }),
  "sector.deleted": z.object({ sectorId: Id }),
  "sector.assigned": z.object({ assignment: AssignmentDto }),
  "assignment.updated": z.object({ assignment: AssignmentDto }),
  "coverage.updated": z.object({ coverage: CoverageDto }),

  "participant.joined": z.object({ participant: ParticipantDto }),
  "participant.updated": z.object({ participant: ParticipantDto }),
  "participant.status": z.object({ participantId: Id, status: ParticipantStatus }),
  "participant.tracking_state": z.object({ participantId: Id, trackingState: TrackingState }),
  "participant.live_mode": z.object({ participantId: Id, live: z.boolean() }),
  /** After an app reinstall: everything of `participantId` now belongs to `intoParticipantId`. */
  "participant.merged": z.object({ participantId: Id, intoParticipantId: Id }),
  /**
   * Someone became or stopped being a member of a group: moved by HQ, approved, or joined with
   * a group invite (issue #151). Goes to the phones of both groups, which announce it; carries
   * no personal data beyond the name group mates already see. `null`: no group (or not yet
   * approved).
   */
  "participant.group_changed": z.object({
    participantId: Id,
    name: z.string(),
    fromGroupId: Id.nullable(),
    toGroupId: Id.nullable(),
  }),

  "sos.raised": z.object({ sos: SosDto }),
  "sos.acknowledged": z.object({ sos: SosDto }),
  "sos.cancelled": z.object({ sos: SosDto }),

  "message.sent": z.object({ message: MessageDto }),
  /** The photo file of a message is on the server; `channel` decides who may see it. */
  "message.photo_ready": z.object({ messageId: Id, channel: Channel, photo: PhotoDto }),
  /**
   * Someone at HQ has read `channel` up to the message with this `seq` (issue #111): the phones
   * mark their own messages as read by HQ. Only when that position moves forward.
   */
  "chat.read_by_hq": z.object({ channel: Channel, seq: z.number().int() }),
  "finding.reported": z.object({ finding: FindingDto }),
  /**
   * `reporterId`: the reporting participant, the only phone that sees the finding; null for a
   * finding HQ entered, whose photo HQ added from a file in the panel (#211).
   */
  "finding.photo_ready": z.object({ findingId: Id, reporterId: Id.nullable(), photo: PhotoDto }),
  /** HQ corrected the place, kind or description (#210). Seen like the report. */
  "finding.updated": z.object({ finding: FindingDto }),
  /** HQ removed it (a mistake, a duplicate, #210): hidden everywhere, kept in the database. */
  "finding.removed": z.object({ findingId: Id, reporterId: Id.nullable() }),

  "recall.issued": z.object({ recall: RecallDto }),
  "recall.confirmed": z.object({ recallId: Id, participantId: Id, confirmedAt: Timestamp }),

  "invite.used": z.object({ inviteId: Id, participantId: Id }),
  "invite.revoked": z.object({ inviteId: Id }),
  "session.revoked": z.object({ participantId: Id, sessionId: Id, graceUntil: Timestamp }),
} as const;

export type ActionEventPayloads = typeof actionEventPayloads;
export type ActionEventType = keyof ActionEventPayloads;
export const ACTION_EVENT_TYPES = Object.keys(actionEventPayloads) as ActionEventType[];

export const ActorType = z.enum(["staff", "participant", "system"]);
export type ActorType = z.infer<typeof ActorType>;

export type ActionEventOf<T extends ActionEventType> = {
  seq: number;
  type: T;
  occurredAt: string;
  actorType: ActorType;
  actorId: string | null;
  payload: z.infer<ActionEventPayloads[T]>;
};

/** Discriminated union over `type`; narrow with `event.type === "sector.created"`. */
export type ActionEvent = { [T in ActionEventType]: ActionEventOf<T> }[ActionEventType];

export type ActionEventPayload<T extends ActionEventType> = z.infer<ActionEventPayloads[T]>;

const envelopeBase = {
  seq: z.number().int().positive(),
  occurredAt: Timestamp,
  actorType: ActorType,
  actorId: z.string().nullable(),
};

/** Runtime schema for one event (used by clients that want to validate the stream). */
export const ActionEventSchema = z.discriminatedUnion(
  "type",
  ACTION_EVENT_TYPES.map((type) =>
    z.object({ ...envelopeBase, type: z.literal(type), payload: actionEventPayloads[type] }),
  ) as unknown as [z.ZodObject, ...z.ZodObject[]],
) as unknown as z.ZodType<ActionEvent>;

// --- Stream (SSE) --------------------------------------------------------------------------

/**
 * SSE event names. `action-event` carries an ActionEvent with `id: seq` and is resumable via
 * Last-Event-ID. `participants` carries ParticipantStateDto[] without an id: positions are
 * state, not history; after (re)connecting the panel refetches the snapshot of participants.
 */
export const STREAM_EVENTS = {
  actionEvent: "action-event",
  participants: "participants",
  ready: "ready",
  /**
   * Keepalive every 15 s. A real event, not an SSE comment, because comments are invisible to
   * JavaScript: the panel uses pings to detect a silent, half-open connection and reconnect.
   */
  ping: "ping",
} as const;

/** The panel reconnects when nothing (not even a ping) arrived for this long. */
export const STREAM_SILENCE_TIMEOUT_MS = 45_000;
